import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, sgd, resolveImage, socialLinks } from "@/lib/api";
import LookCard from "@/components/LookCard";
import ComingSoonTile from "@/components/ComingSoonTile";
import SocialIcons from "@/components/SocialIcons";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

export default function Home() {
  const [looks, setLooks] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [social, setSocial] = useState([]);
  const [email, setEmail] = useState("");
  const [s, setSettings] = useState({});
  const [settingsReady, setSettingsReady] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const { addComplete } = useCart();

  useEffect(() => {
    api.get("/looks").then(({ data }) => {
      setLooks(data);
      setFeatured(data.filter(l => l.is_featured));
    });
    api.get("/social").then(({ data }) => setSocial(data)).catch(() => {});
    api.get("/settings")
      .then(({ data }) => { setSettings(data); setSettingsReady(true); })
      .catch(() => setSettingsReady(true));
  }, []);

  // Scroll to #newsletter if URL hash points there (e.g. navigated from Looks page)
  useEffect(() => {
    if (window.location.hash === "#newsletter") {
      // Wait a tick for the DOM to render after data loads
      setTimeout(() => {
        document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [looks]);

  const today = looks[0];
  const secondFeatured = featured[1];

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast.success("Welcome to ONE LOOK ♡"); setEmail(""); }
    catch { toast.error("Please try again"); }
  };

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0 items-center min-h-[70vh] md:min-h-[85vh]">
          <div className="order-2 md:order-1 px-6 md:pl-16 lg:pl-24 py-14 md:py-0">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
              <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5] mb-6">{s.hero_kicker || "Singapore Fashion · New Collection"}</div>
              <h1 className="font-serif-display text-[46px] md:text-[76px] leading-[1] tracking-tight text-[#4A464A]">
                {s.hero_headline_1 || "Your outfit,"}<br /><em className="not-italic" style={{ color: "#C3B1C5" }}>{s.hero_headline_2 || "already matched."}</em>
              </h1>
              <p className="mt-8 max-w-md text-[15px] leading-relaxed text-[#4A464A]/80">
                {s.hero_body || "Effortless outfits curated to be worn together. Pick your look, put it on, and go."}
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link data-testid="hero-cta-shop" to="/looks" className="px-7 py-3.5 text-[12px] uppercase tracking-editorial inline-flex items-center gap-2" style={{ background: "#4A464A", color: "#FDFBF7" }}>
                  Shop The Looks <ArrowRight size={14} strokeWidth={1.2} />
                </Link>
                <Link data-testid="hero-cta-newin" to="/new-in" className="px-7 py-3.5 text-[12px] uppercase tracking-editorial border" style={{ borderColor: "#4A464A", color: "#4A464A" }}>
                  New In
                </Link>
              </div>
              <div className="mt-14 flex items-center gap-4 text-[11px] uppercase tracking-editorial text-[#4A464A]/60">
                <span>◐</span><span>we did the matching for you</span>
              </div>
            </motion.div>
          </div>
          <div className="order-1 md:order-2 relative">
            {/* Reserved space — cream placeholder prevents layout shift and hides any flash */}
            <div className="relative w-full h-[55vh] md:h-[85vh] overflow-hidden" style={{ background: "#FDFBF7" }} data-testid="hero-image-frame">
              {settingsReady && s.hero_image_url && (
                <img
                  src={resolveImage(s.hero_image_url)}
                  alt="ONE LOOK hero"
                  data-testid="hero-image"
                  fetchpriority="high"
                  decoding="async"
                  onLoad={() => setHeroLoaded(true)}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-out"
                  style={{ opacity: heroLoaded ? 1 : 0 }}
                />
              )}
            </div>
            {(() => {
              const slug = s.hero_caption_look_slug;
              if (slug === "") return null; // explicitly hidden
              const captionLook = looks.find(l => l.slug === (slug || "coffee-date"));
              if (!captionLook) return null;
              return (
                <Link to={`/looks/${captionLook.slug}`} data-testid="hero-caption-card" className="absolute bottom-6 left-6 md:bottom-10 md:left-10 backdrop-blur-md px-4 py-3 hover:bg-white transition-colors" style={{ background: "rgba(253,251,247,0.85)" }}>
                  <div className="text-[10px] uppercase tracking-editorial text-[#C3B1C5]">LOOK {captionLook.number}</div>
                  <div className="font-serif-display text-[16px]">{captionLook.name}</div>
                  <div className="text-[11px] tracking-editorial mt-1">{sgd(captionLook.bundle_price)} · Complete Look</div>
                </Link>
              );
            })()}
          </div>
        </div>
      </section>

      {/* SHOP THE LOOK */}
      <section data-testid="shop-the-look" className="px-6 md:px-10 py-24 md:py-32 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5] mb-3">◈ The Signature</div>
            <h2 className="font-serif-display text-[38px] md:text-[54px] leading-none tracking-tight">{s.shop_the_look_title || "Shop The Look"}</h2>
            <p className="mt-3 text-[14px] italic text-[#4A464A]/70">{s.shop_the_look_subtitle || "we did the matching for you."}</p>
          </div>
          <Link to="/looks" className="text-[11px] uppercase tracking-editorial border-b pb-1" style={{ borderColor: "#4A464A" }}>View all looks →</Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-10">
          {featured.slice(0, 2).map((l, i) => (
            <div key={l.id} data-testid={`featured-look-${l.slug}`} className="group">
              <div className="relative overflow-hidden">
                <img src={resolveImage(l.hero_image)} alt={l.name} className="w-full aspect-[4/5] object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]" />
                <div className="absolute top-4 left-4 text-[10px] uppercase tracking-editorial px-2 py-1" style={{ background: "#FDFBF7" }}>LOOK {l.number}</div>
              </div>
              <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-serif-display text-[26px] leading-tight">{l.name}</div>
                  <div className="text-[13px] mt-1 text-[#4A464A]/70">{l.pieces.map(p => p.name).join(" + ")}</div>
                  <div className="text-[13px] mt-2 tracking-editorial">{sgd(l.bundle_price)} <span className="text-[#4A464A]/50">/ Complete Look</span></div>
                </div>
                <div className="flex gap-2">
                  <Link to={`/looks/${l.slug}`} data-testid={`shop-this-look-${l.slug}`} className="px-4 py-2.5 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Shop This Look</Link>
                  <Link to={`/looks/${l.slug}`} className="px-4 py-2.5 text-[11px] uppercase tracking-editorial border" style={{ borderColor: "#4A464A" }}>View Pieces</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TODAY'S ONE LOOK */}
      {today && (
        <section data-testid="todays-look" className="relative">
          <div className="grid md:grid-cols-5 min-h-[80vh]">
            <div className="md:col-span-3 relative">
              <img src={resolveImage(today.gallery?.[0] || today.hero_image)} alt={today.name} className="w-full h-[70vh] md:h-full object-cover" />
              {/* hotspots */}
              {today.pieces.slice(0, 2).map((p, i) => (
                <motion.div key={p.id} className="absolute" style={{ top: i === 0 ? "32%" : "62%", left: i === 0 ? "42%" : "50%" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.2 }}>
                  <div className="pulse-hotspot w-3 h-3 rounded-full" style={{ background: "#C3B1C5" }} />
                  <div className="mt-2 -ml-16 w-40 backdrop-blur-md p-2.5 text-[11px]" style={{ background: "rgba(253,251,247,0.9)" }}>
                    <div className="font-serif-display text-[13px]">{p.name}</div>
                    <div className="mt-0.5 tracking-editorial">{sgd(p.price)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="md:col-span-2 px-6 md:px-12 py-14 md:py-24 flex flex-col justify-center">
              <div className="text-[11px] tracking-editorial uppercase text-[#4A464A]/60 mb-4">◐ {s.todays_look_title || "Today's One Look"}</div>
              <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.05]">{today.name}</h2>
              <p className="mt-5 text-[14px] leading-relaxed text-[#4A464A]/80 max-w-sm">{today.story || today.description}</p>
              <div className="mt-8 space-y-3">
                {today.pieces.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-[13px] py-2 border-b" style={{ borderColor: "rgba(74,70,74,0.15)" }}>
                    <span>{p.name}</span><span className="tracking-editorial">{sgd(p.price)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-[14px] pt-3 font-serif-display">
                  <span>Complete Look</span><span>{sgd(today.bundle_price)}</span>
                </div>
              </div>
              <Link data-testid="todays-look-cta" to={`/looks/${today.slug}`} className="mt-10 self-start px-7 py-3.5 text-[12px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>
                Get The Look
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* NEW LOOKS */}
      <section data-testid="new-looks" className="px-6 md:px-10 py-24 md:py-32 max-w-[1400px] mx-auto">
        <div className="mb-14">
          <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5] mb-3">◇ Just Dropped</div>
          <h2 className="font-serif-display text-[38px] md:text-[54px] leading-none tracking-tight">{s.new_looks_title || "The First Seven"}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-14">
          {looks.slice(0, 8).map((l, i) => <LookCard key={l.id} look={l} index={i} />)}
          <ComingSoonTile nextNumber={String((looks.length || 0) + 1).padStart(2, "0")} />
        </div>
      </section>

      {/* WEEK IN ONE LOOK removed per owner request — The First Seven grid above is now the main looks section */}

      {/* BRAND STORY */}
      <section data-testid="brand-story" className="px-6 md:px-10 py-28" style={{ background: "#EBE5ED" }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[11px] tracking-editorial uppercase text-[#4A464A]/60 mb-4">◈ Our Story</div>
          <h2 className="font-serif-display text-[36px] md:text-[52px] leading-[1.1] tracking-tight">{s.brand_story_title || "Why ONE LOOK?"}</h2>
          <div className="divider-thin w-24 mx-auto mt-8" />
          <p className="mt-10 text-[15px] leading-[1.9] text-[#4A464A]/85 font-light">
            {s.brand_story_body || "Getting dressed shouldn't be complicated. ONE LOOK was created for women who love looking put-together without spending forever deciding what matches. Every look is thoughtfully paired so you can spend less time styling and more time living."}
          </p>
          <p className="mt-6 italic text-[15px]" style={{ color: "#C3B1C5" }}>one look, effortlessly you.</p>
        </div>
      </section>

      {/* SOCIAL GRID (Shoppable) */}
      <section data-testid="social-grid" className="px-6 md:px-10 py-24 max-w-[1400px] mx-auto">
        {(() => {
          const sl = socialLinks(s);
          const handle = sl.instagramUsername || sl.tiktokUsername;
          const kickerHandle = handle ? `@${handle}` : "";
          const tiles = social.length > 0 ? social : looks.slice(0, 4).map(l => ({
            id: l.id, image: l.hero_image, look_slug: l.slug, caption: `LOOK ${l.number} · ${l.name}`,
            look: { number: l.number, name: l.name, bundle_price: l.bundle_price },
          }));
          return (
            <>
              <div className="text-center mb-10">
                {kickerHandle && <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5] mb-3" data-testid="social-handle-kicker">◐ {kickerHandle}</div>}
                <h2 className="font-serif-display text-[32px] md:text-[44px] tracking-tight">{s.social_title || "Seen in ONE LOOK"}</h2>
                <p className="mt-3 text-[13px] italic text-[#4A464A]/70">tap any photo to shop the look</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                {tiles.map((sp) => (
                  <Link key={sp.id} to={sp.look_slug ? `/looks/${sp.look_slug}` : "#"} data-testid={`social-tile-${sp.id}`} className="block aspect-square overflow-hidden group relative">
                    <img src={resolveImage(sp.image)} alt={sp.caption || "social"} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]" />
                    {sp.look && (
                      <div className="absolute inset-0 flex items-end p-3 md:p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(74,70,74,0.55) 100%)" }}>
                        <div className="text-white">
                          <div className="text-[10px] uppercase tracking-editorial">LOOK {sp.look.number}</div>
                          <div className="font-serif-display text-[15px] leading-tight">{sp.look.name}</div>
                          <div className="text-[11px] tracking-editorial mt-0.5">Shop · S${sp.look.bundle_price?.toFixed(2)}</div>
                        </div>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
              <div className="mt-10 flex flex-col items-center gap-5">
                {sl.instagramUsername && (
                  <a href={sl.instagram} target="_blank" rel="noreferrer" data-testid="social-follow-instagram" className="text-[12px] uppercase tracking-editorial border-b pb-0.5" style={{ borderColor: "#4A464A" }}>
                    Follow @{sl.instagramUsername}
                  </a>
                )}
                <SocialIcons settings={s} variant="home" testPrefix="home-social-icons" />
              </div>
            </>
          );
        })()}
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" data-testid="newsletter" className="px-6 md:px-10 py-28" style={{ background: "#FDFBF7" }}>
        <div className="max-w-lg mx-auto text-center">
          <div className="text-[11px] tracking-editorial uppercase text-[#C3B1C5] mb-4">♡ ONE LOOK Girls</div>
          <h2 className="font-serif-display text-[32px] md:text-[44px] tracking-tight leading-tight">{s.newsletter_title || "Join the ONE LOOK girls"}</h2>
          <p className="mt-5 text-[14px] text-[#4A464A]/75">{s.newsletter_body || "New looks, styling inspiration and little surprises — straight to your inbox. Enjoy 10% off your first look."}</p>
          <form onSubmit={subscribe} className="mt-8 flex items-center border-b" style={{ borderColor: "#C3B1C5" }}>
            <input data-testid="hero-newsletter-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1 bg-transparent py-3 text-[14px] outline-none placeholder:text-[#4A464A]/40" />
            <button data-testid="hero-newsletter-submit" type="submit" className="text-[11px] uppercase tracking-editorial pl-4">Join Us →</button>
          </form>
        </div>
      </section>
    </div>
  );
}
